import { ArrowRight, MessageCircle, ShieldCheck, Clock, FileCheck2, MapPin } from "lucide-react";
import { BRAND } from "@/lib/brand";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#0B1528]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Obra de construcción moderna Amlou"
          className="w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1528] via-[#0B1528]/85 to-[#0B1528]/30" />
        <div className="absolute inset-0 bg-grain opacity-30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40 w-full">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div
            data-testid="hero-eyebrow"
            className="animate-fade-up inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#F26522]/40 bg-[#F26522]/10 backdrop-blur-sm mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#F26522] animate-pulse" />
            <span className="text-[#FFB58C] text-xs font-mono-accent uppercase tracking-[0.2em]">
              {BRAND.location} · Baix Penedès
            </span>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-title"
            className="animate-fade-up delay-100 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-extrabold tracking-tight text-white leading-[1.02]"
          >
            Construimos tus{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#F26522]">ideas</span>
              <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#F26522]/25 -z-0" />
            </span>{" "}
            en realidad.
          </h1>

          {/* Subtitle */}
          <p
            data-testid="hero-subtitle"
            className="animate-fade-up delay-200 mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed"
          >
            Especialistas en <strong className="text-white font-semibold">obra nueva</strong>,{" "}
            <strong className="text-white font-semibold">reformas integrales</strong>,{" "}
            rehabilitación de edificios y todo tipo de fachadas en Cunit, Tarragona y alrededores.
          </p>

          {/* CTAs */}
          <div className="animate-fade-up delay-300 mt-9 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="hero-cta-quote"
              href="#contacto"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#F26522] hover:bg-[#D95212] text-white font-semibold text-base shadow-2xl shadow-orange-900/30 transition-all hover:-translate-y-0.5"
            >
              Solicitar Presupuesto Gratis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              data-testid="hero-cta-whatsapp"
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#25D366] hover:bg-[#20BA57] text-white font-semibold text-base transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Directo
            </a>
          </div>

          {/* Trust badges */}
          <div className="animate-fade-up delay-400 mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {[
              { Icon: ShieldCheck, label: "Garantía y Calidad" },
              { Icon: Clock, label: "Plazos Cumplidos" },
              { Icon: FileCheck2, label: "Precio Claro" },
              { Icon: MapPin, label: "Trato Cercano" },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm"
              >
                <Icon className="w-4 h-4 text-[#F26522] shrink-0" />
                <span className="text-xs sm:text-sm text-white/85 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className="animate-fade-up delay-500 mt-14 grid grid-cols-3 gap-4 max-w-2xl">
          {[
            { num: "15+", label: "Años de experiencia" },
            { num: "100%", label: "Proyectos entregados" },
            { num: "24h", label: "Respuesta presupuesto" },
          ].map((m) => (
            <div
              key={m.label}
              data-testid={`hero-metric-${m.num}`}
              className="border-l-2 border-[#F26522] pl-4"
            >
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-white">
                {m.num}
              </div>
              <div className="text-xs sm:text-sm text-white/60 mt-1 font-medium">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/40 font-mono-accent text-[10px] uppercase tracking-[0.3em]">
        <span>Desliza</span>
        <span className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>
  );
}
